import { useMemo, useState } from 'react';
import { ArrowLeft, CalendarClock, FileText, PackageCheck, RefreshCw } from 'lucide-react';
import {
  type RequisitionLineDto,
  type RequisitionStatus,
} from '@eombor/contracts';
import { Badge, Button, Card, EmptyState, Skeleton, Table, cn, type Column } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { useSurveys } from '../../accounting/model/accounting.queries.js';
import { RequirementDialog } from '../../accounting/ui/RequirementDialog.js';
import { SurveyReviewDialog } from '../../accounting/ui/SurveyReviewDialog.js';
import { useNomenclatures } from '../../catalog/model/catalog.queries.js';
import { useOrgUnits } from '../../org/model/org.queries.js';
import { useWarehouses } from '../../warehouse/model/warehouse.queries.js';
import { useRequisition, useTransition } from '../model/requisition.queries.js';
import {
  needsRefresh,
  requisitionErrorText,
  transitionFailureKind,
} from '../model/requisition-error.js';
import { isOverdue, isStoppedStatus, requisitionStatusTone } from '../model/requisition-status.js';
import { useHandovers } from '../../handover/model/handover.queries.js';
import { useMyWriteOffs } from '../../writeoff/model/write-off.queries.js';
import { HandoverActs } from '../../handover/ui/HandoverActs.js';
import { PhotoPicker } from '../../handover/ui/PhotoPicker.js';
import { ReceiptDialog } from '../../handover/ui/ReceiptDialog.js';
import { AcceptDialog } from './AcceptDialog.js';
import { OrgApproveDialog } from './OrgApproveDialog.js';
import { OrgDecisionPanel } from './OrgDecisionPanel.js';
import { RejectDialog } from './RejectDialog.js';
import { StatusTimeline } from './StatusTimeline.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { storekeepingApi } from '../../storekeeping/api/storekeeping.api.js';
import { LabelPrintButton } from '../../storekeeping/ui/LabelPrintButton.js';
import { NextStepPanel, TransitionBar } from './TransitionBar.js';
import { requisitionStatusText, unitText } from '../../../shared/i18n/labels.js';

/** Miqdorni ekranga chiqarish — `numeric(14,3)` bilan bir xil yumaloqlash. */
const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

/** Sana: "12.08.2026". Vaqt ko'rsatilmaydi — muddat kun aniqligida. */
const formatDate = (iso: string | null): string | null => {
  if (iso === null) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

/** Dalolatnoma YOZILGAN holatlar — foto-hisobot shulardan boshlab bor. */
const HAS_HANDOVER: readonly RequisitionStatus[] = ['HANDED_OVER', 'RECEIPT_CONFIRMED', 'CLOSED'];

export interface RequisitionCardProps {
  requisitionId: string;
  /** Ro'yxatga qaytish — kartochka marshrutlashni bilmaydi. */
  onBack?: () => void;
}

interface FactProps {
  label: string;
  children: React.ReactNode;
}

const Fact = ({ label, children }: FactProps) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <dt className="text-xs text-text-faint">{label}</dt>
    <dd className="min-w-0 text-sm text-text">{children}</dd>
  </div>
);

/**
 * Talabnoma KARTOCHKASI — hujjatning to'liq ko'rinishi.
 *
 * Ekran ikki ustunga bo'linadi va bu ataylab: chapda "nima so'ralgan"
 * (o'zgarmas raqamlar), o'ngda "talabnoma qayerda" (yo'l). Skladchi chap ustunga,
 * rahbar esa o'ng ustunga qaraydi va ikkalasi bir-birini kutmaydi.
 *
 * Pastdagi tugmalar SERVERDAN keladi (`availableTransitions`). Kartochka
 * o'tish qoidalarini bilmaydi va bilishi ham kerak emas — bu qoidalar
 * state machine da, bitta joyda turadi.
 */
export const RequisitionCard = ({ requisitionId, onBack }: RequisitionCardProps) => {
  const card = useRequisition(requisitionId);
  const transition = useTransition(requisitionId);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [requirementOpen, setRequirementOpen] = useState(false);
  /** Qoralama blanki — so'rovnoma ochilishidan oldingi ko'rinish. */
  const [draftOpen, setDraftOpen] = useState(false);

  /*
   * Talabnomaning SO'ROVNOMASI — trebovaniya blanki shundan yasaladi.
   *
   * Ro'yxat so'rovi bilan olinadi, chunki talabnoma javobida so'rovnoma
   * yo'q: bog'lanish teskari tomonda (`survey.requisition_id`) va uni
   * talabnoma javobiga qo'shish `requisition → accounting` bog'liqligini
   * tug'dirardi.
   */
  const surveys = useSurveys({ requisitionId, limit: 1 });
  const survey = surveys.data?.items[0];
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  /*
   * QABUL QILISH oynasi va u bilan birga kelgan suratlar.
   *
   * Suratlar tugmani bosgan paytda olinadi (tugmaning o'zi fayl maydoni),
   * shuning uchun ular oynadan OLDIN paydo bo'ladi va oyna ochilganda unga
   * uzatiladi.
   */
  const [receipt, setReceipt] = useState<{ files: readonly File[] } | null>(null);

  /*
   * DALOLATNOMALAR tarix uchun ham kerak: qadam QAYERDA bajarilgani
   * dalolatnomada yozilgan, o'tish yozuvida emas.
   *
   * So'rov ikki marta ketmaydi — `HandoverActs` ham aynan shu kalitni
   * ishlatadi va javob keshdan keladi.
   */
  const acts = useHandovers(HAS_HANDOVER.includes(card.data?.status ?? 'DRAFT') ? requisitionId : null);
  /* Rasxod aktlari — xodimning O'ZINIKI. Mahsulot bo'linmaga yetib
     borgandan keyingina rasxod bo'lishi mumkin, shuning uchun so'rov
     faqat shu bosqichlardan boshlab yuboriladi. */
  const writeOffs = useMyWriteOffs({ limit: 100 });

  const warehouses = useWarehouses({ limit: 200 });
  const nomenclatures = useNomenclatures({ limit: 200 });
  const { canAnywhere } = usePermission();
  const orgUnits = useOrgUnits();

  const nameOf = useMemo(
    () => new Map((nomenclatures.data?.items ?? []).map((item) => [item.id, item])),
    [nomenclatures.data],
  );
  const warehouseName = (warehouses.data?.items ?? []).find(
    (warehouse) => warehouse.id === card.data?.warehouseId,
  )?.name;
  /* Talabnomani YOZGAN korxona. Ombor MTU niki va butun MTU ga xizmat
     qiladi, ya'ni ombor nomi hujjatning kimniki ekanini AYTMAYDI —
     tasdiqlayotgan odam aynan shu savolga javob izlaydi. */
  const requesterOrgUnit = orgUnits.nameOf(card.data?.requesterOrgUnitId ?? null);

  if (card.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-24 w-full" />
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (card.isError) {
    const missing = transitionFailureKind(card.error) === 'missing';
    return (
      <Card>
        <EmptyState
          title={t(missing ? 'requisition.cardNotFound' : 'requisition.listError')}
          description={t(
            missing ? 'requisition.cardNotFoundHint' : 'requisition.cardNotFoundHint',
          )}
          action={
            <>
              {onBack === undefined ? null : (
                <Button variant="secondary" onClick={onBack}>
                  {t('requisition.back')}
                </Button>
              )}
              <Button onClick={() => void card.refetch()}>{t('requisition.retry')}</Button>
            </>
          }
        />
      </Card>
    );
  }

  const requisition = card.data;
  const overdue = !isStoppedStatus(requisition.status) && isOverdue(requisition.neededBy);

  const gpsOf = (kind: 'ISSUE' | 'RECEIPT'): { lat: number; lng: number } | null =>
    acts.data?.find((act) => act.kind === kind)?.gps ?? null;
  const issueGps = gpsOf('ISSUE');
  const receiptGps = gpsOf('RECEIPT');

  /**
   * Shu talabnoma bo'yicha rasxod qilingan miqdorlar, NOMENKLATURA
   * bo'yicha yig'ilgan.
   *
   * Akt PARTIYAGA tegishli, jadval esa nomenklatura satrlaridan iborat:
   * bitta satr uchun bir necha partiyadan rasxod bo'lishi mumkin va ular
   * qo'shilishi kerak.
   *
   * `CANCELLED` va `REJECTED` sanalmaydi — ular hech nimani hisobdan
   * chiqarmagan.
   */
  const writeOffTotals = new Map<string, { approved: number; pending: number }>();
  for (const act of writeOffs.data?.items ?? []) {
    if (act.requisitionId !== requisitionId) continue;
    if (act.status !== 'APPROVED' && act.status !== 'PENDING') continue;

    const current = writeOffTotals.get(act.nomenclatureId) ?? { approved: 0, pending: 0 };
    if (act.status === 'APPROVED') current.approved += act.qty;
    else current.pending += act.qty;
    writeOffTotals.set(act.nomenclatureId, current);
  }

  const columns: Column<RequisitionLineDto>[] = [
    { key: 'lineNo', header: '№', mono: true, width: '3.5rem' },
    {
      key: 'nomenclatureId',
      header: t('requisition.colNomenclature'),
      render: (line) => {
        const nomenclature = nameOf.get(line.nomenclatureId);
        return (
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-text">
              {nomenclature?.name ?? `#${line.nomenclatureId}`}
            </span>
            {nomenclature === undefined ? null : (
              <span className="tabular text-xs text-text-faint">{nomenclature.code}</span>
            )}
          </span>
        );
      },
    },
    {
      key: 'requestedQty',
      header: t('requisition.colRequested'),
      align: 'right',
      mono: true,
      width: '8rem',
      render: (line) => {
        const unit = nameOf.get(line.nomenclatureId)?.unit;
        return `${qtyText(line.requestedQty)}${unit === undefined ? '' : ` ${unitText(unit)}`}`;
      },
    },
    {
      key: 'orgApprovedQty',
      header: t('requisition.colOrgApproved'),
      align: 'right',
      mono: true,
      /* Kengroq: ustunda son bilan BIRGA uning sababi ham turadi. */
      width: '14rem',
      /*
       * KORXONA tasdiqlagan miqdor — boshliqning qarori.
       *
       * Skladchining ustunidan ALOHIDA turadi va bu ataylab: ular ikki
       * xil savolga javob beradi ("shunchasiga haqlisiz" va "shunchasi
       * bor"). Bitta ustunga siqilsa, "boshliq 5 ta bergan, sklad 3 ta
       * topgan" zanjiri hujjatdan yo'qolardi.
       */
      render: (line) => {
        if (line.orgApprovedQty === null) return <span className="text-text-faint">—</span>;
        if (line.orgApprovedQty === 0) {
          return <Badge tone="danger">{t('requisition.orgNotGranted')}</Badge>;
        }
        const cut = line.orgApprovedQty < line.requestedQty;
        return (
          <span className={cn('inline-flex flex-col items-end gap-0.5', cut ? 'text-warn' : undefined)}>
            {qtyText(line.orgApprovedQty)}
            {/*
              NEGA kesilgani — jadvalning O'ZIDA, izoh ostida.
              Ilgari sabab faqat `title` da turardi: sichqonchani ustiga
              olib borgan odam ko'rardi, qolganlari esa "nega 10?" degan
              savol bilan qolardi. Telefonda esa uni umuman ko'rib
              bo'lmasdi.
            */}
            {line.orgNote === null ? null : (
              <span className="max-w-[12rem] text-right text-xs leading-4 font-normal text-text-muted">
                {line.orgNote}
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'approvedQty',
      header: t('requisition.colApproved'),
      align: 'right',
      mono: true,
      width: '8rem',
      render: (line) =>
        line.approvedQty === null ? (
          <span className="text-text-faint">—</span>
        ) : (
          qtyText(line.approvedQty)
        ),
    },
    {
      key: 'issuedQty',
      header: t('requisition.colIssued'),
      align: 'right',
      mono: true,
      width: '8rem',
      /*
       * HAQIQATDA berilgan miqdor — dalolatnomadan keladi va tasdiqlangandan
       * KAM bo'lishi mumkin. Uni tasdiqlangan miqdordan "hisoblab" ko'rsatish
       * qismli berish faktini yashirardi: hujjat 25 ta deb turgan bo'lardi,
       * omborda esa 20 ta chiqqan.
       */
      render: (line) =>
        line.issuedQty === null ? (
          <span className="text-text-faint">—</span>
        ) : (
          <span className={cn(line.issuedQty < (line.approvedQty ?? line.requestedQty) ? 'text-warn' : undefined)}>
            {qtyText(line.issuedQty)}
          </span>
        ),
    },
  ];

  /*
   * RASXOD — talabnoma YOPILGANDAN keyin ham qoladigan savolga javob:
   * «shundan nechtasini ishlatdik?»
   *
   * Ustun FAQAT aktlar bo'lganda qo'shiladi: rasxod qilinmagan
   * talabnomada u har satrda bo'sh chiziqcha bo'lib turib, jadvalni
   * bekorga kengaytirardi.
   *
   * Ma'lumot `/write-offs/mine` dan keladi, ya'ni uni faqat aktni
   * YOZGAN xodim ko'radi. Aynan shu to'g'ri: savolni ham u beradi —
   * mahsulotni u olgan va u ishlatgan.
   *
   * `StatusTimeline` TEGILMAYDI: u talabnomaning O'Z oqimi, rasxod esa
   * undan keyin va mustaqil boshlanadi.
   */
  if (writeOffTotals.size > 0) {
    columns.push({
      key: 'writeOff',
      header: t('requisition.colWriteOff'),
      align: 'right',
      mono: true,
      width: '11rem',
      render: (line) => {
        const totals = writeOffTotals.get(line.nomenclatureId);
        if (totals === undefined) return <span className="text-text-faint">—</span>;
        return (
          <span className="inline-flex flex-col items-end gap-0.5">
            {totals.approved > 0 ? (
              <span>{t('requisition.writeOffDone', { qty: qtyText(totals.approved) })}</span>
            ) : null}
            {totals.pending > 0 ? (
              <span className="text-xs font-normal text-warn">
                {t('requisition.writeOffPending', { qty: qtyText(totals.pending) })}
              </span>
            ) : null}
          </span>
        );
      },
    });
  }

  const failureKind = transitionFailureKind(transition.error);
  const failureText =
    transition.error === null || transition.error === undefined
      ? null
      : failureKind === 'permission'
        ? t('requisition.errorPermission')
        : failureKind === 'missing'
          ? t('requisition.errorMissing')
          : failureKind === 'conflict'
            ? t('requisition.errorConflict')
            : requisitionErrorText(transition.error, t('requisition.transitionFailed'));

  /**
   * Tugma bosildi.
   *
   * Ikkita qadam so'rovni DARHOL yubormaydi, chunki serverga qo'shimcha
   * ma'lumot kerak va uni odam oynada to'ldiradi: rad etish — sabab,
   * sklad qabul qilish — har satr uchun partiya, katak va miqdor. Server
   * shartlarni baribir qayta tekshiradi; oyna ularni faqat YIG'ADI.
   */
  const start = (to: RequisitionStatus): void => {
    transition.reset();
    if (to === 'REJECTED') {
      setRejectOpen(true);
      return;
    }
    if (to === 'WAREHOUSE_ACCEPTED') {
      setAcceptOpen(true);
      return;
    }
    /* Korxona tasdig'i ham DARHOL yuborilmaydi: boshliq har satrga
       nechta berilishini shu yerda belgilaydi. Ilgari bu qadam bir
       bosishda o'tib ketardi va miqdorni keyin skladchi kesardi — ya'ni
       boshliqning qarori boshqa odamning qo'liga o'tib qolardi. */
    if (to === 'ORG_APPROVED') {
      setApproveOpen(true);
      return;
    }
    /* So'rovnoma TO'G'RIDAN-TO'G'RI ochilmaydi: avval qog'oz ko'rsatiladi.
       Hujjat ochilgandan keyin o'zgarmaydi — nomlar yoki miqdor xato
       ketsa, uni faqat yangi talabnoma bilan tuzatish mumkin bo'lardi. */
    if (to === 'ACCOUNTING_PENDING') {
      setDraftOpen(true);
      return;
    }
    /* Buxgalterning tasdig'i ham qog'ozsiz bo'lmaydi: u imzolaydigan
       hujjatni ko'rmasdan turib tasdiqlay olmaydi va blankdagi raqamni
       ham o'sha yerda qo'yadi. Ilgari bu tugma bir bosishda o'tib
       ketardi — oyna umuman ochilmasdi. */
    if (to === 'ACCOUNTING_APPROVED' && survey !== undefined) {
      setRequirementOpen(true);
      return;
    }
    /* Qabul tasdig'i RASMSIZ bo'lmaydi — server ham shunday deydi
       (`confirm-receipt` rasmsiz 400, umumiy o'tish esa bu holatga
       umuman yo'l qo'ymaydi). Odatda bu yergacha yetib kelinmaydi:
       tugma o'zi fayl maydoni va oyna surat bilan ochiladi. Klaviatura
       yoki eski brauzerdan kelgan bosish esa oynani bo'sh ochadi. */
    if (to === 'RECEIPT_CONFIRMED') {
      setReceipt({ files: [] });
      return;
    }
    transition.mutate({ to });
  };

  return (
    <div className="flex flex-col gap-4">
      <header
        className={cn(
          'flex flex-col gap-4 rounded-[var(--radius-lg)] border border-border',
          'bg-surface p-4 shadow-[var(--shadow-1)]',
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="flex flex-wrap items-center gap-2">
              {onBack === undefined ? null : (
                <Button variant="ghost" size="sm" onClick={onBack}>
                  <ArrowLeft size={14} aria-hidden />
                  {t('requisition.back')}
                </Button>
              )}
              <h1 className="tabular text-xl font-semibold tracking-tight text-text">
                {requisition.docNo}
              </h1>
              <Badge tone={requisitionStatusTone(requisition.status)}>
                {requisitionStatusText(requisition.status)}
              </Badge>
              {overdue ? <Badge tone="danger">{t('requisition.overdue')}</Badge> : null}
            </span>
            <p className="max-w-prose text-sm text-text-muted">{requisition.purpose}</p>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {/*
              TREBOVANIYA — skladchining qog'ozi.
              U so'rovnoma ochilgan zahoti paydo bo'ladi: qog'ozni aynan
              skladchi bosib, imzolatib buxgalterga topshiradi. Buxgalter
              ekraniga o'tib, so'rovnomani qidirib topish esa boshqa
              odamning ekraniga borish bo'lardi.
            */}
            {survey === undefined ? null : (
              <Button variant="secondary" size="sm" onClick={() => setRequirementOpen(true)}>
                <FileText size={14} aria-hidden />
                {/* Raqam tugmaning O'ZIDA: mahsulot aynan shu raqam
                    bo'yicha beriladi va uni ko'rish uchun oynani ochish
                    kerak bo'lardi. To'ldirilmaguncha faqat nom turadi. */}
                {survey.formNo === null
                  ? t('accounting.requirementAction')
                  : `${t('accounting.requirementAction')} № ${survey.formNo}`}
              </Button>
            )}
            {/* ETIKETKALAR — berilgan har partiya uchun QR. Skladchi uni
                mahsulot bilan birga beradi; bo'linma esa yo'qolganini qayta chiqaradi. */}
            {HAS_HANDOVER.includes(requisition.status) && (canAnywhere('stock.issue') || canAnywhere('storeroom.manage')) ? (
              <LabelPrintButton
                variant="secondary"
                label={t('label.printAll')}
                load={() => storekeepingApi.requisitionLabels(requisition.id)}
              />
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              loading={card.isFetching}
              onClick={() => void card.refetch()}
            >
              <RefreshCw size={14} aria-hidden />
              {t('requisition.refresh')}
            </Button>
          </div>
        </div>

        <dl className="grid gap-3 border-t border-border pt-3 sm:grid-cols-2 xl:grid-cols-4">
          {/*
            Korxona OMBORDAN OLDIN: «kimdan kelgan» savoliga javob «qayerga
            yozilgan» dan muhimroq — ombor ro'yxatdagi hamma hujjatda bir
            xil turadi.

            YO'QLIGI HAM YOZILADI va qator yashirilmaydi. Korxonasiz hujjat
            hech kimning korxona doirasiga tushmaydi va uning ustidagi
            tugmalar ishlamay qoladi. Qator chizilmasa, sabab ko'rinmas edi:
            odam «nega yubora olmayapman?» degan savol bilan yolg'iz
            qolardi. Endi javob hujjatning o'zida turadi.
          */}
          <Fact label={t('requisition.fieldOrgUnit')}>
            {requesterOrgUnit === null ? (
              <span className="text-warn">{t('requisition.orgUnitMissing')}</span>
            ) : (
              requesterOrgUnit
            )}
          </Fact>
          <Fact label={t('requisition.fieldWarehouse')}>
            {warehouseName ?? `#${requisition.warehouseId}`}
          </Fact>
          {/*
            ISM SERVERDAN keladi.
            Ilgari kartochka uni o'zi topishga urinardi va faqat
            hujjatning EGASI o'zi ko'rganda topardi — boshqa hamma
            «Xodim #4780» degan identifikatorni ko'rardi. Hujjatning eng
            ko'p o'qiladigan maydoni shunday bo'lib qolgan edi.
          */}
          <Fact label={t('requisition.fieldRequester')}>
            {requisition.requesterName ??
              t('requisition.timelineActor', { id: requisition.requesterUserId })}
            {requisition.requesterPosition === null ? null : (
              <span className="text-text-muted"> · {requisition.requesterPosition}</span>
            )}
          </Fact>
          {/* «Через кого» — u trebovaniya blankiga tushadi va shuning
              uchun kartochkada ham ko'rinib turishi kerak: xato yozilgani
              qog'ozda emas, shu yerda bilinsin. */}
          {requisition.throughPerson === null ? null : (
            <Fact label={t('requisition.fieldThroughPerson')}>{requisition.throughPerson}</Fact>
          )}
          <Fact label={t('requisition.fieldNeededBy')}>
            <span
              className={cn(
                'tabular inline-flex items-center gap-1.5',
                overdue ? 'text-danger' : undefined,
              )}
            >
              <CalendarClock size={14} aria-hidden />
              {formatDate(requisition.neededBy) ?? t('requisition.noDate')}
            </span>
          </Fact>
          <Fact label={requisition.closedAt === null ? t('requisition.fieldNote') : t('requisition.fieldClosedAt')}>
            {requisition.closedAt === null ? (
              (requisition.note ?? <span className="text-text-faint">—</span>)
            ) : (
              <span className="tabular">{formatDate(requisition.closedAt)}</span>
            )}
          </Fact>
        </dl>

        {requisition.rejectReason === null ? null : (
          <p
            className={cn(
              'rounded-[var(--radius-sm)] border border-danger/25 bg-danger-soft',
              'px-3 py-2 text-sm text-danger',
            )}
          >
            <span className="font-semibold">{t('requisition.rejectReasonLabel')}: </span>
            {requisition.rejectReason}
          </p>
        )}
      </header>

      {/*
        SIZDAN NIMA KUTILMOQDA va QADAM TUGMALARI — sahifaning eng boshida,
        yonma-yon.

        Ilgari ular hujjatning oxirida turardi. Talabnoma esa uzun: satrlar
        jadvali, korxona qarori, tarix va dalolatnomalar. Ekran ochilganda
        odam birinchi navbatda jadvalni ko'rardi va o'zidan nima
        kutilayotganini bilish uchun pastga aylantirishi kerak edi —
        holbuki u shu ekranga aynan shuning uchun kelgan.

        Yo'riqnoma tugmadan yuqorida: avval NIMA qilish kerakligi o'qiladi,
        keyin bosiladi.
      */}
      <div className="flex flex-col gap-3">
        <NextStepPanel available={requisition.availableTransitions} from={requisition.status} />

        <TransitionBar
          available={requisition.availableTransitions}
          from={requisition.status}
          pending={transition.isPending ? (transition.variables?.to ?? null) : null}
          /*
           * "Qabul qildim" — oddiy tugma EMAS, fayl maydoni.
           *
           * Bir tegishda telefonning kamerasi ochiladi, surat olinadi va
           * oyna allaqachon shu surat bilan chiqadi. Oddiy tugma bo'lsa,
           * kamera faqat ikkinchi tegishdan keyin ochilardi.
           */
          slots={{
            RECEIPT_CONFIRMED: (
              <PhotoPicker
                label={t('requisition.action.RECEIPT_CONFIRMED')}
                icon={<PackageCheck size={16} aria-hidden />}
                capture="environment"
                tone="primary"
                onPick={(files) => {
                  transition.reset();
                  setReceipt({ files });
                }}
              />
            ),
          }}
          /* So'rovnoma yuklanmaguncha buxgalterning tugmasi bosilmaydi.
             Aks holda `survey` hali `undefined` bo'lgan payt bosilsa,
             tasdiq oynasiz o'tib ketardi — aynan shu holat bir marta
             bo'lgan: tugma bosilgan, modal chiqmagan. */
          disabled={requisition.status === 'ACCOUNTING_PENDING' && surveys.isPending}
          onTransition={start}
        />

        {/*
          Xato SAHIFADA faqat oyna yopiq bo'lganda chiziladi.
          Oyna ochiq bo'lsa, u xatoni O'ZI ko'rsatadi (`error` proplari) va
          odam aynan o'sha yerga qarab turadi. Ikkovi birga chizilsa, ekranda
          bir xil matn ikki marta turardi va ekran o'quvchi ham ikkalasini
          o'qib berardi.
        */}
        {failureText === null || acceptOpen || approveOpen || rejectOpen ? null : (
          <p
            role="alert"
            className={cn(
              'flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-sm)]',
              'border px-3 py-2 text-sm',
              needsRefresh(transition.error)
                ? 'border-warn/25 bg-warn-soft text-warn'
                : 'border-danger/25 bg-danger-soft text-danger',
            )}
          >
            {failureText}
            {/* 409 dan keyin qayta urinish befoyda — kartochkani yangilash kerak. */}
            {needsRefresh(transition.error) ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  transition.reset();
                  void card.refetch();
                }}
              >
                {t('requisition.refresh')}
              </Button>
            ) : null}
          </p>
        )}
      </div>

      {/* KORXONANING QARORI — jadvaldan YUQORIDA.
          "Nega 23 emas, 10?" degan savol hujjatning eng ko'p so'raladigan
          savoli va javob jadvalning ichida bir ustunda ko'milib qolmasligi
          kerak. Blok faqat haqiqatan kesilgan bo'lsa chiziladi. */}
      <OrgDecisionPanel
        lines={requisition.lines}
        transitions={requisition.transitions}
        nameOf={(id) => nameOf.get(id)}
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <Card title={t('requisition.cardLinesTitle')} padded={false}>
          <Table
            caption={t('requisition.cardLinesTitle')}
            columns={columns}
            rows={requisition.lines}
            rowKey={(line) => line.id}
            className="rounded-none border-0"
          />
        </Card>

        <div className="flex flex-col gap-4">
          <Card title={t('requisition.timelineTitle')}>
            <StatusTimeline
              transitions={requisition.transitions}
              currentStatus={requisition.status}
              /* Berish va qabul — mahsulot qo'ldan qo'lga o'tadigan
                 yagona ikki qadam va faqat ularning joyi bor. */
              locations={{
                ...(issueGps === null ? {} : { HANDED_OVER: issueGps }),
                ...(receiptGps === null ? {} : { RECEIPT_CONFIRMED: receiptGps }),
              }}
            />
          </Card>

          {/* Foto-hisobot berish bo'lgandan KEYIN paydo bo'ladi: undan oldin
              bo'sh bo'lim kartochkada faqat joy egallardi. */}
          {HAS_HANDOVER.includes(requisition.status) ? (
            <Card title={t('handover.actsTitle')}>
              <HandoverActs requisitionId={requisition.id} />
            </Card>
          ) : null}
        </div>
      </div>

      <OrgApproveDialog
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        warehouseId={requisition.warehouseId}
        requisitionId={requisition.id}
        lines={requisition.lines}
        nameOf={(id) => nameOf.get(id)}
        pending={transition.isPending}
        error={failureText}
        onConfirm={(approvals) =>
          transition.mutate(
            { to: 'ORG_APPROVED', approvals },
            { onSuccess: () => setApproveOpen(false) },
          )
        }
      />

      <AcceptDialog
        open={acceptOpen}
        onClose={() => setAcceptOpen(false)}
        warehouseId={requisition.warehouseId}
        lines={requisition.lines}
        nameOf={(id) => nameOf.get(id)}
        pending={transition.isPending}
        error={failureText}
        onConfirm={(placements) =>
          transition.mutate(
            { to: 'WAREHOUSE_ACCEPTED', lines: placements },
            { onSuccess: () => setAcceptOpen(false) },
          )
        }
      />

      {/*
        QABUL QILISH — talabnomani yopadigan qadam, rasm bilan.
        Oyna faqat kerak bo'lganda quriladi: `initialFiles` o'zgarganda
        ichidagi effekt ishga tushadi va yopiq oyna uchun bu bekor ish
        bo'lardi.
      */}
      {receipt === null ? null : (
        <ReceiptDialog
          open
          requisitionId={requisition.id}
          initialFiles={receipt.files}
          onClose={() => setReceipt(null)}
          onDone={() => setReceipt(null)}
        />
      )}

      <RejectDialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        pending={transition.isPending}
        error={failureText}
        onConfirm={(reason) =>
          transition.mutate(
            { to: 'REJECTED', reason },
            { onSuccess: () => setRejectOpen(false) },
          )
        }
      />

      {/* Blank oynasi so'rovnoma bo'lgandagina chiziladi: usiz uni
          yasab bo'lmaydi va bo'sh oyna ochish savolga javob emas. */}
      {/*
        SO'ROVNOMA OCHILGAN — blank raqami bilan, faqat ko'rish uchun.
        Hujjat o'zgarmaydi, ya'ni bu yerda tasdiqlaydigan narsa yo'q.
      */}
      {survey === undefined ? null : (
        <SurveyReviewDialog
          open={requirementOpen}
          survey={survey}
          onClose={() => setRequirementOpen(false)}
          nameOf={(id) => nameOf.get(id)}
          /* Kim nima qila olishini SERVER aytadi (`availableTransitions`) —
             o'tish qoidalarining nusxasi bu yerda saqlanmaydi. */
          canApprove={requisition.availableTransitions.includes('ACCOUNTING_APPROVED')}
          canReject={requisition.availableTransitions.includes('REJECTED')}
          pending={transition.isPending}
          error={failureText}
          onApprove={() =>
            transition.mutate(
              { to: 'ACCOUNTING_APPROVED' },
              { onSuccess: () => setRequirementOpen(false) },
            )
          }
          onReject={(reason) =>
            transition.mutate(
              { to: 'REJECTED', reason },
              { onSuccess: () => setRequirementOpen(false) },
            )
          }
        />
      )}

      {/*
        SO'ROVNOMA HALI OCHILMAGAN — QORALAMA va uning ostida "ochish".
        Skladchi qog'ozni ko'zdan kechirib, keyin hujjatni ochadi:
        ochilgandan keyin u o'zgarmaydi va xatoni faqat yangi talabnoma
        bilan tuzatish mumkin bo'lardi.
      */}
      <RequirementDialog
        open={draftOpen}
        source={{ requisition: requisitionId }}
        docNo={requisition.docNo}
        onClose={() => setDraftOpen(false)}
        confirm={{
          label: t('requisition.action.ACCOUNTING_PENDING'),
          pending: transition.isPending,
          /* Oyna FAQAT muvaffaqiyatda yopiladi: xato bo'lsa xabar shu
             yerda ko'rinishi kerak, aks holda qog'oz yo'qolib, "nima
             bo'ldi?" degan savol javobsiz qolardi. */
          onConfirm: () =>
            transition.mutate({ to: 'ACCOUNTING_PENDING' }, { onSuccess: () => setDraftOpen(false) }),
        }}
      />
    </div>
  );
};
